import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import Offcanvas from 'bootstrap/js/dist/offcanvas';
import Dropdown from 'bootstrap/js/dist/dropdown';
import Collapse from 'bootstrap/js/dist/collapse';

// example importing other Bootstrap Javascript Module
import Modal from 'bootstrap/js/dist/modal';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, ScrollToPlugin);

// the bundle is alive -- unlike .jb-js, which header.php drops after its fade
// failsafe, this stays. See docs/js-runtime.md#jb-js-booted
document.documentElement.classList.add('jb-js-booted');

/**
 * Shared runtime namespace.
 * layout.js is the ONLY file that imports GSAP/Bootstrap, then publishes the
 * single instance on window.DRG. Page bundles (source/js/pages/*.js) read from
 * window.DRG so layout + page animations share ONE GSAP instance.
 * Page files must NOT import gsap again (that creates a second instance).
 */
window.DRG = {
    gsap,
    ScrollTrigger,
    ScrollSmoother,
    Modal,
    Offcanvas,
    Dropdown,
    Collapse,
    smoother: null, // set below once the wrapper is confirmed present
    fn: {}, // global helpers — attach reusable functions below
};

/**
 * Global layout logic (runs on every page).
 * Put navbar, offcanvas, global scroll animations, etc. here.
 */

/**
 * Scroll-triggered fade up.
 *
 * Two shapes, both opt-in from markup so no module is hardcoded here:
 *   data-jb-fade        one element, its own trigger
 *   data-jb-fade-group  a container whose children reveal in batches
 *   data-jb-fade-item   inside a group, reveal THESE instead of the children
 *
 * Never animate a .swiper-slide or a .swiper-wrapper. GSAP would write transform
 * onto elements Swiper is already translating, and the slide's own
 * `transition-property: transform` would smear it. Tagging something INSIDE a
 * slide is fine -- Swiper never touches it -- which is what the selector form of
 * the group attribute is for.
 *
 * Above-the-fold sections should not be tagged at all -- there is nothing to
 * scroll to, and hiding an LCP element behind opacity: 0 until JS boots trades a
 * Core Web Vital for an animation nobody sees. That is why the hero opts out.
 */
const JB_FADE_RISE_DESKTOP = 40;
const JB_FADE_RISE_MOBILE = 24;
const JB_FADE_DURATION = 0.8;
const JB_FADE_STAGGER = 0.12;
const JB_FADE_EASE = 'power2.out';

/**
 * Fires a little before the element is fully in view.
 *
 * clamp() is load-bearing, not decoration. An element near the foot of the page
 * can compute a start position BEYOND the maximum scroll -- the last button on
 * the home page wanted 5611 against a 5540 ceiling -- so its onEnter never runs
 * and it stays hidden for good. clamp() pins the computed start inside the
 * scrollable range, which costs nothing anywhere else.
 */
const JB_FADE_START = 'clamp(top 85%)';

const jbMatches = (query) => window.matchMedia(query).matches;

/**
 * Resolved when the tween fires rather than when the trigger is built, so a
 * resize before the element arrives still gets the right distance -- and so
 * there is no matchMedia context to revert. A reverting context would reset an
 * already-played element to invisible while its once:true trigger was already
 * dead, leaving it stuck there.
 *
 * Reduced motion keeps the fade and drops the slide. Movement is what causes
 * vestibular symptoms; an opacity change barely registers. Returning 0 keeps one
 * code path, and the reveal still happens.
 */
const jbFadeRise = () => {
    if (jbMatches('(prefers-reduced-motion: reduce)')) return 0;

    return jbMatches('(max-width: 767.98px)') ? JB_FADE_RISE_MOBILE : JB_FADE_RISE_DESKTOP;
};

const jbFadePlay = (targets, stagger = 0) =>
    gsap.fromTo(
        targets,
        { autoAlpha: 0, y: jbFadeRise() },
        { autoAlpha: 1, y: 0, duration: JB_FADE_DURATION, ease: JB_FADE_EASE, stagger }
    );

// set inline so the header failsafe cannot reveal an element mid-wait and leave
// it to snap back to invisible when it finally enters
const jbFadeArm = (targets) => gsap.set(targets, { autoAlpha: 0 });

// a second init pass over the same root must not double-trigger
const jbFadeClaim = (el) => {
    if (el.dataset.jbFadeBound) return false;

    el.dataset.jbFadeBound = '1';

    return true;
};

// a start clamped to 0 can never be crossed from scroll 0, so onEnter would
// never run -- see docs/js-runtime.md#fade-already-entered
const jbFadeEntered = (st) => st.scroll() >= st.start;

const jbFadeInit = (root = document) => {
    gsap.utils.toArray('[data-jb-fade]', root).forEach((el) => {
        if (!jbFadeClaim(el)) return;

        jbFadeArm(el);

        const st = ScrollTrigger.create({
            trigger: el,
            start: JB_FADE_START,
            once: true,
            onEnter: () => jbFadePlay(el),
        });

        if (jbFadeEntered(st)) {
            st.kill();
            jbFadePlay(el);
        }
    });

    /**
     * batch() groups by arrival time, not by DOM structure, so a responsive grid
     * reveals row by row at every breakpoint without anyone computing what a row
     * is. One card per row on mobile collapses the stagger on its own.
     */
    gsap.utils.toArray('[data-jb-fade-group]', root).forEach((group) => {
        if (!jbFadeClaim(group)) return;

        /**
         * The group's DIRECT CHILDREN, unless it contains elements marked
         * data-jb-fade-item -- then those are what reveal.
         *
         * That exists for the one case direct children cannot serve: a Swiper.
         * The slides and the wrapper belong to Swiper -- writing transform onto
         * either fights its translate3d, and `.swiper-slide` also carries
         * `transition-property: transform`, which would smear every frame GSAP
         * writes. A child of the slide is untouched by Swiper, so the insight
         * track marks its cards and leaves the slides alone.
         *
         * A marker attribute rather than a selector on the group, because the
         * CSS start state has to hide exactly the same elements before JS boots
         * and cannot read an arbitrary selector out of an attribute. See the
         * fade block in source/scss/layout.scss.
         */
        const marked = Array.from(group.querySelectorAll('[data-jb-fade-item]'));
        const items = marked.length ? marked : Array.from(group.children);

        if (!items.length) return;

        jbFadeArm(items);

        const triggers = ScrollTrigger.batch(items, {
            start: JB_FADE_START,
            once: true,
            onEnter: (batch) => jbFadePlay(batch, JB_FADE_STAGGER),
        });

        const entered = triggers.filter(jbFadeEntered);

        if (!entered.length) return;

        entered.forEach((st) => st.kill());
        jbFadePlay(entered.map((st) => st.trigger), JB_FADE_STAGGER);
    });
};

window.DRG.fn.fadeInit = jbFadeInit;

/**
 * Page-wide smooth scrolling. See docs/js-runtime.md#scrollsmoother
 */
const JB_SMOOTH_LAG = 1;

const jbSmootherInit = () => {
    const wrapper = document.getElementById('smooth-wrapper');

    // inertia moves the whole page, so reduced motion opts out entirely and
    // native scrolling (plus scroll-padding-top) takes over
    if (!wrapper || jbMatches('(prefers-reduced-motion: reduce)')) return null;

    return ScrollSmoother.create({
        wrapper,
        content: '#smooth-content',
        smooth: JB_SMOOTH_LAG,
        smoothTouch: 0, // touch keeps native scrolling
        ignoreMobileResize: true,
    });
};

// before jbFadeInit, so every fade trigger is built against the final scroller
window.DRG.smoother = jbSmootherInit();

// scripts are enqueued in the footer, so the DOM is already parsed -- the guard
// is for the day someone moves them to the head or adds defer
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => jbFadeInit(), { once: true });
} else {
    jbFadeInit();
}

/**
 * Mobile navbar drill-down.
 *
 * Bootstrap's Dropdown drives open/close at both breakpoints; the slide is pure
 * CSS. Only two behaviours need wiring: the back control, and resetting an open
 * panel when the offcanvas closes.
 */
const jbNavbarPanel = document.getElementById('offcanvasNavbar');
const jbNavbarToggler = document.querySelector('.jb-navbar__toggler');

const jbHideDropdown = (toggle) => {
    if (toggle) Dropdown.getOrCreateInstance(toggle).hide();
};

/**
 * Bootstrap's Offcanvas does not manage aria-expanded -- only Collapse and
 * Dropdown do. Setting it here serves two purposes: screen readers get the
 * open/closed state, and the CSS swaps the hamburger glyph for the X off the
 * same attribute.
 */
jbNavbarPanel?.addEventListener('show.bs.offcanvas', () => {
    jbNavbarToggler?.setAttribute('aria-expanded', 'true');
});

jbNavbarPanel?.addEventListener('hidden.bs.offcanvas', () => {
    jbNavbarToggler?.setAttribute('aria-expanded', 'false');
});

document.querySelectorAll('.jb-navbar__back').forEach((backButton) => {
    backButton.addEventListener('click', () => {
        const menu = backButton.closest('.dropdown-menu');
        jbHideDropdown(menu?.parentElement?.querySelector('[data-bs-toggle="dropdown"]'));
    });
});

// without this, closing the offcanvas mid-drill-down means reopening it shows
// the sub-panel instead of the root list
jbNavbarPanel?.addEventListener('hidden.bs.offcanvas', () => {
    jbNavbarPanel
        .querySelectorAll('[data-bs-toggle="dropdown"][aria-expanded="true"]')
        .forEach(jbHideDropdown);
});

/**
 * In-page anchor scrolling. See docs/anchor-navigation.md
 */
const JB_ANCHOR_SETTLE_MS = 100;

// the bar is fixed, so its viewport bottom IS the offset an anchor must clear --
// and it already includes the admin bar's own push
const jbAnchorOffset = () =>
    document.querySelector('.jb-navbar')?.getBoundingClientRect().bottom || 0;

// menu items are stored as /about-us while WordPress canonicalises the page to
// /about-us/, so a raw pathname comparison never matches
const jbSamePath = (a, b) => a.replace(/\/+$/, '') === b.replace(/\/+$/, '');

// the navbar panel is .offcanvas-xl, so plain '.offcanvas' misses it -- every
// responsive variant has to be named
const JB_OFFCANVAS_SELECTOR =
    '.offcanvas, .offcanvas-sm, .offcanvas-md, .offcanvas-lg, .offcanvas-xl, .offcanvas-xxl';

const jbAnchorTarget = (hash) => {
    if (!hash || hash === '#') return null;

    try {
        return document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch {
        return null; // malformed percent-encoding
    }
};

const jbAnchorScroll = (target, smooth = true) => {
    const smoother = window.DRG.smoother;

    if (!smoother) {
        target.scrollIntoView(); // scroll-padding-top supplies the offset
        return;
    }

    smoother.scrollTo(target, smooth, `top ${jbAnchorOffset()}px`);
};

// Bootstrap locks scrolling by setting overflow on body, which no longer owns
// the scroll -- pausing the smoother is what actually holds the page still
jbNavbarPanel?.addEventListener('show.bs.offcanvas', () => {
    window.DRG.smoother?.paused(true);
});

/**
 * A modal is position: fixed, and ScrollSmoother transforms #smooth-content --
 * so a dialog rendered inside a module is positioned against that transform and
 * lands off-screen. Moving it to <body> puts it back on the viewport.
 * See docs/js-runtime.md#modals
 */
const jbModalDetach = (root = document) => {
    root.querySelectorAll('.modal').forEach((modal) => {
        if (modal.parentElement !== document.body) document.body.appendChild(modal);
    });
};

jbModalDetach();
window.DRG.fn.modalDetach = jbModalDetach;

// every modal, not just today's -- delegated on document so a dialog rendered
// after boot is covered without registering anything of its own
document.addEventListener('show.bs.modal', () => {
    window.DRG.smoother?.paused(true);
});

document.addEventListener('hidden.bs.modal', () => {
    window.DRG.smoother?.paused(false);
});

// a revealed collapse makes the document taller, so the smoother's height and
// every trigger's start / end are measured again -- see docs/js-runtime.md#collapse
document.addEventListener('shown.bs.collapse', () => {
    ScrollTrigger.refresh();
});

/**
 * Members-only insight cards on touch screens: the first tap reveals the overlay,
 * a second tap follows the card's link once it has one -- a tap never jumps
 * straight to sign-in. Hover already does this with a mouse.
 * See docs/components/insight-card.md#locked
 */
const JB_NO_HOVER = window.matchMedia('(hover: none)');

document.addEventListener('click', (event) => {
    const card = event.target.closest?.('.jb-insight-card--locked');

    document.querySelectorAll('.jb-insight-card--locked.is-revealed').forEach((revealed) => {
        if (revealed !== card) revealed.classList.remove('is-revealed');
    });

    if (!card || !JB_NO_HOVER.matches || card.classList.contains('is-revealed')) return;

    event.preventDefault();
    card.classList.add('is-revealed');
});

jbNavbarPanel?.addEventListener('hidden.bs.offcanvas', () => {
    window.DRG.smoother?.paused(false);
});

document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href*="#"]');

    if (!link || link.hasAttribute('data-bs-toggle')) return;

    // an anchor on another page is an ordinary navigation
    if (link.host !== window.location.host) return;
    if (!jbSamePath(link.pathname, window.location.pathname)) return;

    const target = jbAnchorTarget(link.hash);

    if (!target) return;

    event.preventDefault();

    // 'show' is what tells us the responsive panel is actually open -- above its
    // breakpoint the same element is just the desktop nav
    const panel = link.closest(JB_OFFCANVAS_SELECTOR);
    const offcanvas = panel?.classList.contains('show') ? Offcanvas.getInstance(panel) : null;

    if (!offcanvas) {
        jbAnchorScroll(target);
        return;
    }

    // scrolling under an open panel fights its backdrop, so close it first
    panel.addEventListener('hidden.bs.offcanvas', () => jbAnchorScroll(target), { once: true });
    offcanvas.hide();
});

/**
 * Landing on /page#section. The browser's own jump happens before GSAP takes
 * over the scroll, so the position has to be reapplied once -- without easing,
 * since the visitor asked to arrive there rather than travel there.
 */
if (window.location.hash) {
    const target = jbAnchorTarget(window.location.hash);

    if (target) {
        window.setTimeout(() => {
            ScrollTrigger.refresh();
            jbAnchorScroll(target, false);
        }, JB_ANCHOR_SETTLE_MS);
    }
}
